import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import api from '../../services/api';
import './AuditLogs.css';

// ─── Formatting helpers ────────────────────────────────────────────────────────

const stripHtml = (str) => {
  if (!str) return '';
  return str
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
};

const formatKey = (key) =>
  key
    .replace(/_id$/, '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatValue = (val, maxLen = 80) => {
  if (val === null || val === undefined) return '-';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'object') {
    if (Array.isArray(val)) return val.length ? `[${val.length} items]` : '-';
    return '-';
  }
  const str = String(val);
  const clean = /<[^>]+>/.test(str) ? stripHtml(str) : str;
  return clean.length > maxLen ? clean.slice(0, maxLen) + '…' : clean;
};

// Fields skipped in the compact table view (too large / HTML content)
const SKIP_KEYS_TABLE = new Set(['description', 'content', 'body', 'html', 'details']);

// Returns total number of meaningful fields across old + new values
const countFields = (log) => {
  const keys = new Set([
    ...Object.keys(log.new_values || {}),
    ...Object.keys(log.old_values || {}),
  ]);
  return keys.size;
};

// ─── Compact table cell — max 2 non-verbose fields ────────────────────────────

const CompactDetails = ({ log, onViewAll }) => {
  const newVals = log.new_values || {};
  const oldVals = log.old_values || {};
  const allKeys = Object.keys(newVals).length ? Object.keys(newVals) : Object.keys(oldVals);
  const visibleKeys = allKeys.filter((k) => !SKIP_KEYS_TABLE.has(k)).slice(0, 2);
  const totalFields = countFields(log);
  const hasMore = totalFields > visibleKeys.length;

  if (visibleKeys.length === 0 && log.details) {
    const clean = /<[^>]+>/.test(log.details) ? stripHtml(log.details) : log.details;
    const trimmed = clean.length > 80 ? clean.slice(0, 80) + '…' : clean;
    return (
      <div className="details-compact">
        <span className="log-details">{trimmed}</span>
        {clean.length > 80 && (
          <button className="details-view-btn" onClick={onViewAll}>View</button>
        )}
      </div>
    );
  }

  if (visibleKeys.length === 0) {
    return <span className="no-details">-</span>;
  }

  const vals = Object.keys(newVals).length ? newVals : oldVals;

  return (
    <div className="details-compact">
      <div className="change-details">
        {visibleKeys.map((key) => (
          <div key={key} className="change-item">
            <span className="change-key">{formatKey(key)}:</span>
            {oldVals[key] !== undefined && newVals[key] !== undefined && oldVals[key] !== newVals[key] && (
              <>
                <span className="change-old">{formatValue(oldVals[key])}</span>
                <span className="change-arrow">→</span>
              </>
            )}
            <span className="change-new">{formatValue(vals[key])}</span>
          </div>
        ))}
      </div>
      {hasMore && (
        <button className="details-view-btn" onClick={onViewAll}>
          +{totalFields - visibleKeys.length} more
        </button>
      )}
    </div>
  );
};

// ─── Drawer — full detail view ─────────────────────────────────────────────────

const DetailDrawer = ({ log, onClose }) => {
  if (!log) return null;

  const newVals = log.new_values || {};
  const oldVals = log.old_values || {};
  const allKeys = [
    ...new Set([...Object.keys(newVals), ...Object.keys(oldVals)]),
  ];

  const changedKeys = allKeys.filter(
    (k) => newVals[k] !== undefined && oldVals[k] !== undefined && String(newVals[k]) !== String(oldVals[k])
  );
  const addedKeys = allKeys.filter((k) => newVals[k] !== undefined && oldVals[k] === undefined);
  const removedKeys = allKeys.filter((k) => oldVals[k] !== undefined && newVals[k] === undefined);

  const renderRow = (key, oldVal, newVal, type) => {
    const isHtml = (v) => v && /<[^>]+>/.test(String(v));
    const cleanOld = isHtml(oldVal) ? stripHtml(String(oldVal)) : oldVal !== undefined ? String(oldVal) : null;
    const cleanNew = isHtml(newVal) ? stripHtml(String(newVal)) : newVal !== undefined ? String(newVal) : null;

    return (
      <div key={key} className={`drawer-field drawer-field--${type}`}>
        <div className="drawer-field-key">{formatKey(key)}</div>
        <div className="drawer-field-values">
          {type === 'changed' && (
            <>
              <span className="drawer-val drawer-val--old">{cleanOld ?? '-'}</span>
              <span className="drawer-val-arrow">→</span>
              <span className="drawer-val drawer-val--new">{cleanNew ?? '-'}</span>
            </>
          )}
          {type === 'added' && (
            <span className="drawer-val drawer-val--new">{cleanNew ?? '-'}</span>
          )}
          {type === 'removed' && (
            <span className="drawer-val drawer-val--old">{cleanOld ?? '-'}</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer">
        <div className="drawer-header">
          <div className="drawer-title-group">
            <span className="drawer-entity-type">{log.entity_type}</span>
            {log.entity_name && <span className="drawer-entity-name">{log.entity_name}</span>}
          </div>
          <button className="drawer-close" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="drawer-meta">
          <div className="drawer-meta-row">
            <span className="drawer-meta-label">Action</span>
            <span className="badge badge-warning">{log.action.replace(/_/g, ' ')}</span>
          </div>
          <div className="drawer-meta-row">
            <span className="drawer-meta-label">Time</span>
            <span>{format(new Date(log.created_at), 'MMM d, yyyy HH:mm:ss')}</span>
          </div>
          <div className="drawer-meta-row">
            <span className="drawer-meta-label">User</span>
            <span>{log.user ? `${log.user.first_name} ${log.user.last_name}` : 'System'}</span>
          </div>
          {log.project_name && (
            <div className="drawer-meta-row">
              <span className="drawer-meta-label">Project</span>
              <span className="drawer-project">{log.project_name}</span>
            </div>
          )}
        </div>

        <div className="drawer-body">
          {changedKeys.length > 0 && (
            <div className="drawer-section">
              <div className="drawer-section-title">Changed</div>
              {changedKeys.map((k) => renderRow(k, oldVals[k], newVals[k], 'changed'))}
            </div>
          )}
          {addedKeys.length > 0 && (
            <div className="drawer-section">
              <div className="drawer-section-title">Added</div>
              {addedKeys.map((k) => renderRow(k, undefined, newVals[k], 'added'))}
            </div>
          )}
          {removedKeys.length > 0 && (
            <div className="drawer-section">
              <div className="drawer-section-title">Removed</div>
              {removedKeys.map((k) => renderRow(k, oldVals[k], undefined, 'removed'))}
            </div>
          )}
          {log.details && (
            <div className="drawer-section">
              <div className="drawer-section-title">Notes</div>
              <div className="drawer-notes">
                {/<[^>]+>/.test(log.details) ? stripHtml(log.details) : log.details}
              </div>
            </div>
          )}
          {changedKeys.length === 0 && addedKeys.length === 0 && removedKeys.length === 0 && !log.details && (
            <div className="drawer-empty">No details available for this entry.</div>
          )}
        </div>
      </div>
    </>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────

const AuditLogs = () => {
  const { user } = useSelector((state) => state.auth);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [filters, setFilters] = useState({ action: '', entity_type: '', project_name: '' });
  const [projectNames, setProjectNames] = useState([]);
  const [drawerLog, setDrawerLog] = useState(null);

  const fetchProjectNames = useCallback(async () => {
    try {
      const response = await api.get('/audit/projects');
      setProjectNames(response.data || []);
    } catch (error) {
      console.error('Failed to fetch project names:', error);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('page_size', 20);
      if (filters.action) params.append('action', filters.action);
      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      if (filters.project_name) params.append('project_name', filters.project_name);

      const response = await api.get(`/audit?${params.toString()}`);
      setLogs(response.data.logs);
      setPagination({
        page: response.data.page,
        totalPages: response.data.total_pages,
        total: response.data.total,
      });
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, filters]);

  useEffect(() => { fetchProjectNames(); }, [fetchProjectNames]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Close drawer on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setDrawerLog(null); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const getActionBadge = (action) => {
    if (action.includes('created')) return 'badge-success';
    if (action.includes('deleted')) return 'badge-danger';
    if (action.includes('updated') || action.includes('changed')) return 'badge-warning';
    return 'badge-primary';
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.action) params.append('action', filters.action);
      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      if (filters.project_name) params.append('project_name', filters.project_name);

      const response = await api.get(`/audit/export?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_logs_${format(new Date(), 'yyyy-MM-dd_HHmmss')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Audit logs exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export audit logs');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="audit-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Audit Logs</h1>
          <p className="page-subtitle">Track all system activities and changes</p>
        </div>
        <div className="page-actions">
          <button
            className="btn btn-secondary" style={{ marginTop: '0px' }}
            onClick={handleExportCSV}
            disabled={exporting || logs.length === 0}
            title={logs.length === 0 ? 'No logs to export' : 'Export filtered logs to CSV'}
          >
            {exporting ? (
              <><span className="btn-spinner-small"></span>Exporting...</>
            ) : (
              <><span className="export-icon">📥</span>Export to CSV</>
            )}
          </button>
        </div>
      </div>

      <div className="page-content">
        {/* Filters */}
        <div className="filters-bar">
          <select
            className="form-select"
            value={filters.project_name}
            onChange={(e) => { setFilters({ ...filters, project_name: e.target.value }); setPagination({ ...pagination, page: 1 }); }}
          >
            <option value="">All Projects</option>
            {projectNames.map((name) => (<option key={name} value={name}>{name}</option>))}
          </select>
          <select
            className="form-select"
            value={filters.entity_type}
            onChange={(e) => { setFilters({ ...filters, entity_type: e.target.value }); setPagination({ ...pagination, page: 1 }); }}
          >
            <option value="">All Entity Types</option>
            <option value="user">Users</option>
            <option value="project">Projects</option>
            <option value="task">Tasks</option>
            <option value="sprint">Sprints</option>
            <option value="workflow">Workflows</option>
            <option value="project_member">Project Members</option>
          </select>
          <select
            className="form-select"
            value={filters.action}
            onChange={(e) => { setFilters({ ...filters, action: e.target.value }); setPagination({ ...pagination, page: 1 }); }}
          >
            <option value="">All Actions</option>
            <optgroup label="User Actions">
              <option value="user_created">User Created</option>
              <option value="user_updated">User Updated</option>
              <option value="user_deleted">User Deleted</option>
              <option value="user_login">User Login</option>
              <option value="user_logout">User Logout</option>
            </optgroup>
            <optgroup label="Project Actions">
              <option value="project_created">Project Created</option>
              <option value="project_updated">Project Updated</option>
              <option value="project_deleted">Project Deleted</option>
              <option value="project_archived">Project Archived</option>
            </optgroup>
            <optgroup label="Task Actions">
              <option value="task_created">Task Created</option>
              <option value="task_updated">Task Updated</option>
              <option value="task_deleted">Task Deleted</option>
              <option value="task_status_changed">Task Status Changed</option>
            </optgroup>
            <optgroup label="Sprint Actions">
              <option value="sprint_created">Sprint Created</option>
              <option value="sprint_updated">Sprint Updated</option>
              <option value="sprint_started">Sprint Started</option>
              <option value="sprint_completed">Sprint Completed</option>
              <option value="sprint_deleted">Sprint Deleted</option>
            </optgroup>
            <optgroup label="Member Actions">
              <option value="member_added">Member Added</option>
              <option value="member_role_changed">Member Role Changed</option>
              <option value="member_removed">Member Removed</option>
            </optgroup>
            <optgroup label="Workflow Actions">
              <option value="workflow_changed">Workflow Changed</option>
              <option value="unauthorized_workflow_access">Unauthorized Workflow Access</option>
            </optgroup>
          </select>
          {(filters.project_name || filters.entity_type || filters.action) && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => { setFilters({ action: '', entity_type: '', project_name: '' }); setPagination({ ...pagination, page: 1 }); }}
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* Table */}
        {loading ? (
          <div className="loading-state">
            <span className="spinner"></span>
            <p>Loading audit logs...</p>
          </div>
        ) : logs.length > 0 ? (
          <>
            <div className="audit-table-container">
              <table className="table audit-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>User</th>
                    <th>Action</th>
                    <th>Entity</th>
                    <th>Project</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} className={drawerLog?.id === log.id ? 'row-active' : ''}>
                      <td className="timestamp">
                        {format(new Date(log.created_at), 'MMM d, yyyy HH:mm:ss')}
                      </td>
                      <td>
                        {log.user ? (
                          <span>{log.user.first_name} {log.user.last_name}</span>
                        ) : (
                          <span className="system">System</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${getActionBadge(log.action)}`}>
                          {log.action.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td>
                        <div className="entity-info">
                          <span className="entity-type">{log.entity_type}</span>
                          {log.entity_name && <span className="entity-name">{log.entity_name}</span>}
                          {!log.entity_name && log.entity_id && <span className="entity-id">#{log.entity_id}</span>}
                        </div>
                      </td>
                      <td className="project-name">
                        {log.project_name || <span className="no-project">-</span>}
                      </td>
                      <td className="details">
                        <CompactDetails log={log} onViewAll={() => setDrawerLog(log)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="pagination">
                <button
                  className="btn btn-sm btn-secondary"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                >Previous</button>
                <span className="page-info">Page {pagination.page} of {pagination.totalPages}</span>
                <button
                  className="btn btn-sm btn-secondary"
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                >Next</button>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state"><p>No audit logs found</p></div>
        )}
      </div>

      {/* Detail Drawer */}
      <DetailDrawer log={drawerLog} onClose={() => setDrawerLog(null)} />
    </div>
  );
};

export default AuditLogs;
